import { useTranslation } from "react-i18next";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Wallet } from "lucide-react";
import { Skeleton } from "@/shared/components/ui/skeleton";
import { ApiError } from "@/shared/api/http-client";
import { useCurrentUser } from "@/shared/auth/useAuth";
import { Role } from "@/shared/types/role";
import { formatCoordinates } from "@/shared/lib/format-coordinates";
import { FoundingPartnerBadge } from "@/features/founding/FoundingPartnerBadge";
import { useFoundingAccount } from "@/features/founding/use-founding-account";
import { useVenueDetail } from "./use-venue-detail";
import { TestPartitionBadge } from "@/shared/components/TestPartitionBadge";
import { VenueStatusBadge } from "./VenueStatusBadge";
import { VenueTypeBadge } from "./VenueTypeBadge";
import { VenueQrActions } from "./VenueQrActions";
import { formatDateShort, formatVenueLocation } from "./format-venue";

const WEEKDAY_KEYS = [
  "venuesDirectory.weekday.0",
  "venuesDirectory.weekday.1",
  "venuesDirectory.weekday.2",
  "venuesDirectory.weekday.3",
  "venuesDirectory.weekday.4",
  "venuesDirectory.weekday.5",
  "venuesDirectory.weekday.6",
] as const;

export function VenueDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const currentUser = useCurrentUser();
  // A venue's owner is a venue owner by definition, so the payout-history link
  // is gated purely by the viewer's role. The finance slice is ADMIN + FINANCE,
  // but this page is ADMIN + SUPPORT (`SECTION_ROLES.venueDirectory`), so ADMIN
  // is the only viewer that can be here and follow the link.
  const canViewPayouts = currentUser?.role === Role.WANDERFIT_ADMIN;
  const { data: venue, isLoading, isError, error } = useVenueDetail(id);

  // Owner's account-level Founding Partner badge (ADR-0010 §9). All their
  // venues carry it. The founding-account read is ADMIN + FINANCE on the API
  // and FINANCE cannot open this page, so only ADMIN reads it; SUPPORT would
  // 403, so the read never fires for them.
  const canReadFounding = currentUser?.role === Role.WANDERFIT_ADMIN;
  const founding = useFoundingAccount(
    venue?.owner.id ?? "",
    Boolean(canReadFounding && venue?.owner.id),
  );

  if (isLoading) {
    return (
      <div data-testid="venue-detail-loading" className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-48 w-full" />
      </div>
    );
  }

  if (isError) {
    const status = error instanceof ApiError ? error.status : undefined;
    return (
      <div data-testid="venue-detail-error" className="space-y-4">
        <BackLink label={t("venuesDirectory.detail.back")} />
        <div className="rounded-lg border border-destructive/30 bg-destructive/5 p-6 text-center">
          <p className="font-sans text-sm font-medium text-destructive">
            {status === 404
              ? t("venuesDirectory.detail.notFound")
              : t("venuesDirectory.detail.error")}
          </p>
        </div>
      </div>
    );
  }

  if (!venue) return null;

  return (
    <div data-testid="venue-detail-screen" className="space-y-6">
      <BackLink label={t("venuesDirectory.detail.back")} testId="venue-detail-back" />

      <header className="flex flex-wrap items-start justify-between gap-4">
        <div className="space-y-2">
          <p className="brand-eyebrow">{t("venuesDirectory.detail.eyebrow")}</p>
          <h1 className="text-3xl">{venue.name}</h1>
          <div className="flex flex-wrap items-center gap-2">
            <VenueTypeBadge type={venue.type} />
            <VenueStatusBadge status={venue.status} />
            <TestPartitionBadge isTest={venue.isTest} testId="venue-detail-test-badge" />
            {canReadFounding && (
              <FoundingPartnerBadge
                active={founding.data?.foundingPartner ?? false}
                testId="venue-detail-founding-badge"
              />
            )}
          </div>
        </div>
        <VenueQrActions venue={venue} />
      </header>

      <section
        data-testid="venue-detail-profile"
        className="rounded-lg border border-border bg-card p-6"
      >
        <h2 className="brand-eyebrow mb-4">{t("venuesDirectory.detail.details")}</h2>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("venuesDirectory.detail.location")}
            value={formatVenueLocation(venue) || "—"}
          />
          <Field label={t("venuesDirectory.detail.zipCode")} value={venue.zipCode || "—"} />
          <Field
            label={t("venuesDirectory.detail.coordinates")}
            value={formatCoordinates(venue.latitude, venue.longitude) || "—"}
          />
          <Field
            label={t("venuesDirectory.detail.created")}
            value={formatDateShort(venue.createdAt, i18n.language) || "—"}
          />
          <Field
            label={t("venuesDirectory.detail.updated")}
            value={formatDateShort(venue.updatedAt, i18n.language) || "—"}
          />
          <Field label={t("venuesDirectory.detail.id")} value={venue.id} mono />
          <div className="sm:col-span-2">
            <Field
              label={t("venuesDirectory.detail.description")}
              value={venue.description || "—"}
            />
          </div>
        </dl>
      </section>

      <section
        data-testid="venue-detail-owner"
        className="rounded-lg border border-border bg-card p-6"
      >
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <h2 className="brand-eyebrow">{t("venuesDirectory.detail.owner")}</h2>
          {canViewPayouts && (
            <Link
              to={`/finance/owners/${venue.owner.id}/payouts`}
              data-testid="view-owner-payouts"
              className="inline-flex items-center gap-2 rounded-md border border-input bg-transparent px-3 py-2 font-sans text-sm font-medium text-foreground shadow-sm hover:bg-muted"
            >
              <Wallet className="h-4 w-4" />
              {t("finance.ownerPayouts.viewLink")}
            </Link>
          )}
        </div>
        <dl className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field
            label={t("venuesDirectory.detail.ownerName")}
            value={venue.owner.displayName || "—"}
          />
          <Field label={t("venuesDirectory.detail.ownerEmail")} value={venue.owner.email || "—"} />
          <Field label={t("venuesDirectory.detail.ownerId")} value={venue.owner.id} mono />
        </dl>
      </section>

      {venue.hours.length > 0 && (
        <section
          data-testid="venue-detail-hours"
          className="rounded-lg border border-border bg-card p-6"
        >
          <h2 className="brand-eyebrow mb-4">{t("venuesDirectory.detail.hours")}</h2>
          <ul className="space-y-1">
            {venue.hours.map((h) => (
              <li
                key={h.dayOfWeek}
                className="flex justify-between font-sans text-sm text-foreground"
              >
                <span className="text-muted-foreground">{t(WEEKDAY_KEYS[h.dayOfWeek] ?? "")}</span>
                <span>
                  {h.isClosed
                    ? t("venuesDirectory.detail.closed")
                    : `${h.openTime} – ${h.closeTime}`}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}

      {venue.amenities.length > 0 && (
        <section
          data-testid="venue-detail-amenities"
          className="rounded-lg border border-border bg-card p-6"
        >
          <h2 className="brand-eyebrow mb-4">{t("venuesDirectory.detail.amenities")}</h2>
          <div className="flex flex-wrap gap-2">
            {venue.amenities.map((a) => (
              <span
                key={a.id}
                className="rounded-full border border-border px-2.5 py-0.5 font-sans text-xs text-foreground"
              >
                {a.name}
              </span>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}

function BackLink({ label, testId }: { readonly label: string; readonly testId?: string }) {
  return (
    <Link
      to="/venues-directory"
      data-testid={testId}
      className="inline-flex items-center gap-1 font-sans text-sm text-muted-foreground hover:text-foreground"
    >
      <ArrowLeft className="h-4 w-4" />
      {label}
    </Link>
  );
}

interface FieldProps {
  readonly label: string;
  readonly value: string;
  readonly mono?: boolean;
}

function Field({ label, value, mono }: FieldProps) {
  return (
    <div>
      <dt className="font-sans text-xs font-medium uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd
        className={
          mono
            ? "mt-1 font-mono text-xs font-normal text-foreground"
            : "mt-1 font-sans text-sm font-normal text-foreground"
        }
      >
        {value}
      </dd>
    </div>
  );
}
